/**
 * useAlertFilters Hook
 * Manages filter state
 */

import { useState, useCallback } from 'react';
import type { AlertFilters } from '../types';

export interface UseAlertFiltersReturn {
  filters: AlertFilters;
  updateFilter: <K extends keyof AlertFilters>(key: K, value: AlertFilters[K]) => void;
  resetFilters: () => void;
}

const getDefaultFilters = (): AlertFilters => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  return {
    searchTerm: '',
    priorityFilter: '',
    rectificationFilter: '',
    accuracyFilter: '',
    sortBy: 'created_at',
    currentPage: 1,
    selectedDate: yesterday.toISOString().split('T')[0]
  };
};

export const useAlertFilters = (): UseAlertFiltersReturn => {
  const [filters, setFilters] = useState<AlertFilters>(getDefaultFilters());

  const updateFilter = useCallback(<K extends keyof AlertFilters>(
    key: K,
    value: AlertFilters[K]
  ) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key !== 'currentPage' && key !== 'sortBy' ? { currentPage: 1 } : {})
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(getDefaultFilters());
  }, []);

  return {
    filters,
    updateFilter,
    resetFilters
  };
};
